import { useCallback, useState } from 'react';

import { PromoCodeErrorField, PromoCodeErrors } from '../../../stores/types/promoCode.interface';

const NO_ERRORS: PromoCodeErrors = {};

/**
 * Per-card auxiliary state (validation errors, expand/collapse, in-flight save) keyed
 * by an id — `localId` for not-yet-saved create drafts, `promoCodeId` for edits of saved
 * DRAFT codes. The create and edit flows each own one instance, so they share identical
 * behavior and can't drift apart. Cards default to expanded; `reset` drops a card's
 * state once it's saved, deleted, or cancelled.
 */
export const usePromoCodeCardState = () => {
  const [errorsById, setErrorsById] = useState<Record<string, PromoCodeErrors>>({});
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const setErrors = useCallback((id: string, errors: PromoCodeErrors) => {
    setErrorsById((prev) => ({ ...prev, [id]: errors }));
  }, []);

  const clearError = useCallback((id: string, field: PromoCodeErrorField) => {
    setErrorsById((prev) => ({ ...prev, [id]: { ...prev[id], [field]: undefined } }));
  }, []);

  const setExpanded = useCallback((id: string, value: boolean) => {
    setExpandedById((prev) => ({ ...prev, [id]: value }));
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedById((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));
  }, []);

  const reset = useCallback((id: string) => {
    setErrorsById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExpandedById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const getErrors = useCallback(
    (id: string): PromoCodeErrors => errorsById[id] ?? NO_ERRORS,
    [errorsById],
  );

  const isExpanded = useCallback((id: string): boolean => expandedById[id] ?? true, [expandedById]);

  return {
    savingId,
    setSavingId,
    setErrors,
    clearError,
    setExpanded,
    toggleExpanded,
    reset,
    getErrors,
    isExpanded,
  };
};
